import React from 'react';
import JourneyRail, { JourneyRailProps } from './JourneyRail';
import JourneyRailSlim from './JourneyRailSlim';
import { useSidebarRail, WIZARD_JOURNEY_RAIL } from '../../hooks/useSidebarRail';

/**
 * The wizard's left journey, in whichever of its two shapes the user last
 * chose (remembered per browser, default the slim rail):
 *   - rail (default): the step markers keep their place, and hovering or
 *     focusing them floats the full journey OVER the step content. The main
 *     column does not move — a panel that appears under the pointer must not
 *     shove the page the pointer was heading for.
 *   - pinned: the full rail is back in flow at `w-step-rail` and the layout
 *     makes room for it, exactly as the wizard shipped.
 *
 * Inside an RFQ the step content is the work and the journey is orientation,
 * which is why this one starts collapsed while the app shell's navigation
 * starts open. Both get their behaviour from the same hook; nothing else is
 * shared between the two shells.
 */
export type WizardJourneyNavProps = Omit<JourneyRailProps, 'pinned' | 'onTogglePin' | 'className'>;

export const WizardJourneyNav: React.FC<WizardJourneyNavProps> = ({ steps, locked, onStepSelect, ...rest }) => {
  const rail = useSidebarRail(WIZARD_JOURNEY_RAIL);

  return (
    <div
      ref={rail.containerRef}
      className={`relative z-30 h-full shrink-0 ${rail.pinned ? 'w-step-rail' : 'w-nav-rail-slim'}`}
      onPointerEnter={rail.handleEnter}
      onPointerLeave={rail.handleLeave}
      onFocus={rail.handleFocus}
      onBlur={rail.handleBlur}
    >
      {rail.pinned ? (
        <JourneyRail
          {...rest}
          steps={steps}
          locked={locked}
          onStepSelect={onStepSelect}
          pinned
          onTogglePin={rail.togglePinned}
          className="w-step-rail"
        />
      ) : (
        <>
          <JourneyRailSlim
            steps={steps}
            locked={locked}
            onStepSelect={onStepSelect}
            pinned={false}
            onTogglePin={rail.togglePinned}
            pinButtonRef={rail.railButtonRef}
            className="w-nav-rail-slim"
          />
          {/* Overlay, not a column: absolute inside the rail's own slot, so
              the step content keeps every pixel it had. Visibility rides the
              same transition as opacity so the panel fades out instead of
              being yanked, while staying out of the tab order when closed. */}
          <div
            className={`absolute inset-y-0 left-0 transition-[opacity,transform,visibility] duration-150 ease-out motion-reduce:transition-none ${
              rail.open ? 'visible translate-x-0 opacity-100' : 'invisible -translate-x-s3 opacity-0'
            }`}
            aria-hidden={rail.open ? undefined : true}
          >
            <JourneyRail
              {...rest}
              steps={steps}
              locked={locked}
              onStepSelect={onStepSelect}
              pinned={false}
              onTogglePin={rail.togglePinned}
              className="w-step-rail shadow-floating"
            />
          </div>
        </>
      )}
    </div>
  );
};

export default WizardJourneyNav;
